import { useEffect, useMemo, useState } from 'react';
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Spinner,
} from '@nextui-org/react';
import { checkType, CtyButton, CtyShowToast } from '@circuitry-ai/doc-ui';
import {
  useCreateBusinessToolMutation,
  useGetBusinessToolByIdQuery,
  useGetCredentialsListQuery,
  useTestBusinessToolMutation,
  useUpdateBusinessToolMutation,
  type BusinessToolTestResponse,
} from '@circuitry-ai/doc-data';
import {
  buildCreateBusinessToolPayload,
  createDefaultBusinessToolForm,
  findUnresolvedPlaceholders,
  getAvailablePlaceholders,
  mapBusinessToolDetailToForm,
  normalizeToolName,
  type AuthFormState,
  type BusinessToolFormState,
  type ParameterFormRow,
  type PreRequestFormRow,
} from './businessToolFormUtils';
import { BusinessToolBasicAndParamsSections } from './BusinessToolBasicAndParamsSections';
import { BusinessToolPreRequestsSection } from './BusinessToolPreRequestsSection';
import { BusinessToolApiAndResponseSections } from './BusinessToolApiAndResponseSections';
import { BusinessToolTestPanel } from './BusinessToolTestPanel';

export interface CreateBusinessToolModalProps {
  readonly isOpen: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly existingCacheKeys?: string[];
  /** When set, modal loads the tool and saves via PUT (edit mode). */
  readonly toolId?: string | null;
}

function collectWarnings(form: BusinessToolFormState): string[] {
  const warnings: string[] = [];
  const unresolved = findUnresolvedPlaceholders(form);
  if (unresolved.length > 0) {
    warnings.push(
      `Unresolved placeholders: ${unresolved.map((placeholder) => `{{${placeholder}}}`).join(', ')}`
    );
  }
  if (form.response_body_format === 'sse' && !form.response_extract_path.trim()) {
    warnings.push('SSE selected without extract path — AI will receive the raw stream.');
  }
  return warnings;
}

function buildInitialTestParams(form: BusinessToolFormState): Record<string, string> {
  const params: Record<string, string> = {};
  for (const param of form.parameters) {
    const name = param.name.trim();
    if (name) params[name] = '';
  }
  return params;
}

export function CreateBusinessToolModal({
  isOpen,
  onOpenChange,
  existingCacheKeys = [],
  toolId = null,
}: CreateBusinessToolModalProps) {
  const isEditMode = Boolean(toolId);
  const [form, setForm] = useState<BusinessToolFormState>(createDefaultBusinessToolForm);
  const [savedToolId, setSavedToolId] = useState<string | null>(null);
  const [testParams, setTestParams] = useState<Record<string, string>>({});
  const [testResult, setTestResult] = useState<BusinessToolTestResponse | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);

  const [createBusinessTool, { isLoading: isCreating }] = useCreateBusinessToolMutation();
  const [updateBusinessTool, { isLoading: isUpdating }] = useUpdateBusinessToolMutation();
  const [testBusinessTool, { isLoading: isTesting }] = useTestBusinessToolMutation();
  const {
    data: toolDetail,
    isLoading: isDetailLoading,
    isError: isDetailError,
    error: detailError,
  } = useGetBusinessToolByIdQuery(toolId as string, {
    skip: !isOpen || !toolId,
  });
  const { data: credentialsList, isLoading: isCredentialsLoading } = useGetCredentialsListQuery(
    undefined,
    { skip: !isOpen }
  );
  const credentialOptions = Array.isArray(credentialsList) ? credentialsList : [];

  const activeToolId = toolId || savedToolId;
  const showTestPanel = Boolean(activeToolId);

  useEffect(() => {
    if (!isOpen) return;
    if (isEditMode && toolDetail) {
      const mapped = mapBusinessToolDetailToForm(toolDetail);
      setForm(mapped);
      setSavedToolId(toolId);
      setTestParams(buildInitialTestParams(mapped));
      setTestResult(null);
      setFormError(null);
      setWarnings([]);
    } else if (!isEditMode) {
      setForm(createDefaultBusinessToolForm());
      setSavedToolId(null);
      setTestParams({});
      setTestResult(null);
      setFormError(null);
      setWarnings([]);
    }
  }, [isOpen, isEditMode, toolDetail, toolId]);

  useEffect(() => {
    if (!isOpen || !isDetailError) return;
    const err = detailError as { data?: { message?: string }; message?: string } | undefined;
    setFormError(err?.data?.message ?? err?.message ?? 'Failed to load business tool.');
  }, [isOpen, isDetailError, detailError]);

  const normalizedName = normalizeToolName(form.name);
  const availablePlaceholders = useMemo(() => getAvailablePlaceholders(form), [form]);
  const unresolvedPlaceholders = useMemo(() => findUnresolvedPlaceholders(form), [form]);

  const resetForm = () => {
    setForm(createDefaultBusinessToolForm());
    setSavedToolId(null);
    setTestParams({});
    setTestResult(null);
    setFormError(null);
    setWarnings([]);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) resetForm();
    onOpenChange(open);
  };

  const onFormChange = (patch: Partial<BusinessToolFormState>) => {
    setForm((prev) => ({ ...prev, ...patch }));
  };

  const onUpdateAuth = (patch: Partial<AuthFormState>) => {
    setForm((prev) => ({
      ...prev,
      authentication: { ...prev.authentication, ...patch },
    }));
  };

  const onUpdateParameter = (id: string, patch: Partial<ParameterFormRow>) => {
    setForm((prev) => ({
      ...prev,
      parameters: prev.parameters.map((param) => (param.id === id ? { ...param, ...patch } : param)),
    }));
  };

  const onUpdatePreRequest = (id: string, patch: Partial<PreRequestFormRow>) => {
    setForm((prev) => ({
      ...prev,
      pre_requests: prev.pre_requests.map((pre) => (pre.id === id ? { ...pre, ...patch } : pre)),
    }));
  };

  const handleSave = async () => {
    const { payload, errors } = buildCreateBusinessToolPayload(form);
    setWarnings(collectWarnings(form));

    if (errors.length > 0 || !payload) {
      setFormError(errors[0] ?? 'Please fix form errors before saving.');
      return;
    }

    setFormError(null);
    try {
      if (isEditMode && toolId) {
        await updateBusinessTool({ toolId, body: payload }).unwrap();
        CtyShowToast(checkType.success, 'Business tool updated successfully');
        setSavedToolId(toolId);
        setTestParams((prev) => {
          const next = buildInitialTestParams(form);
          for (const key of Object.keys(next)) {
            if (prev[key] != null) next[key] = prev[key];
          }
          return next;
        });
      } else {
        const response = await createBusinessTool(payload).unwrap();
        const createdId = String(response._id ?? response.id ?? '');
        CtyShowToast(checkType.success, 'Business tool created successfully');
        if (createdId) {
          setSavedToolId(createdId);
          setTestParams(buildInitialTestParams(form));
        } else {
          handleOpenChange(false);
        }
      }
    } catch (error: unknown) {
      const err = error as { data?: { message?: string }; message?: string };
      const message =
        err?.data?.message ??
        err?.message ??
        (isEditMode
          ? 'Failed to update business tool.'
          : 'Failed to create business tool. Name may already exist.');
      setFormError(message);
      CtyShowToast(checkType.error, message);
    }
  };

  const handleTest = async () => {
    if (!activeToolId) return;
    try {
      const parameters: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(testParams)) {
        parameters[key] = value;
      }
      const result = await testBusinessTool({
        toolId: activeToolId,
        body: { parameters },
      }).unwrap();
      setTestResult(result);
      CtyShowToast(
        result.success === false ? checkType.warning : checkType.success,
        'Test completed'
      );
    } catch (error: unknown) {
      const err = error as { data?: { message?: string }; message?: string };
      const message = err?.data?.message ?? err?.message ?? 'Tool test failed';
      CtyShowToast(checkType.error, message);
    }
  };

  const isSaving = isCreating || isUpdating;
  const modalTitle = isEditMode ? 'Edit Business Tool' : 'Create Business Tool';

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      placement="center"
      backdrop="blur"
      size="5xl"
      scrollBehavior="inside"
      classNames={{
        base: 'max-h-[92vh]',
        body: 'py-4',
        header: 'border-b border-divider',
        footer: 'border-t border-divider',
      }}
    >
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1">{modalTitle}</ModalHeader>
            <ModalBody className="gap-6">
              {isEditMode && isDetailLoading ? (
                <div className="flex items-center justify-center py-16">
                  <Spinner size="lg" label="Loading tool..." />
                </div>
              ) : (
                <>
                  <BusinessToolBasicAndParamsSections
                    form={form}
                    normalizedName={normalizedName}
                    availablePlaceholders={availablePlaceholders}
                    unresolvedPlaceholders={unresolvedPlaceholders}
                    onFormChange={onFormChange}
                    onUpdateParameter={onUpdateParameter}
                    onSetParameters={(parameters) => onFormChange({ parameters })}
                  />
                  <BusinessToolPreRequestsSection
                    preRequests={form.pre_requests}
                    existingCacheKeys={existingCacheKeys}
                    onSetPreRequests={(pre_requests) => onFormChange({ pre_requests })}
                    onUpdatePreRequest={onUpdatePreRequest}
                  />
                  <BusinessToolApiAndResponseSections
                    form={form}
                    credentialOptions={credentialOptions}
                    isCredentialsLoading={isCredentialsLoading}
                    onFormChange={onFormChange}
                    onUpdateAuth={onUpdateAuth}
                  />

                  {formError ? <p className="text-sm text-danger">{formError}</p> : null}
                  {warnings.length > 0 ? (
                    <div className="rounded-lg border border-warning bg-warning/10 px-3 py-2 flex flex-col gap-1">
                      {warnings.map((warning) => (
                        <p key={warning} className="text-xs text-warning">
                          {warning}
                        </p>
                      ))}
                    </div>
                  ) : null}

                  {showTestPanel ? (
                    <BusinessToolTestPanel
                      testParams={testParams}
                      testResult={testResult}
                      isTesting={isTesting}
                      onTestParamChange={(paramName, value) =>
                        setTestParams((prev) => ({ ...prev, [paramName]: value }))
                      }
                      onTest={handleTest}
                    />
                  ) : null}
                </>
              )}
            </ModalBody>
            <ModalFooter>
              <CtyButton variant="light" onPress={onClose}>
                {showTestPanel && !isEditMode ? 'Close' : 'Cancel'}
              </CtyButton>
              <CtyButton
                color="primary"
                isLoading={isSaving}
                isDisabled={isEditMode && isDetailLoading}
                onPress={handleSave}
              >
                {isEditMode ? 'Update Tool' : 'Save Tool'}
              </CtyButton>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}

export default CreateBusinessToolModal;
